'use client';

import { ProductDetailGiftPayload } from '@/api/types/product';
import {
  GiftDialogFlow,
  GiftSingleDialog,
  type GiftSingleDialogData,
} from '@/components/GiftDetailsDialog';
import Image from '@/components/Image';
import {
  buildProductDetailGiftViewModel,
  PDP_GIFT_INTERACTION_NAMES,
  reportProductDetailGiftInteraction,
} from '@/domain/product/gift-display';
import { useTranslations } from '@/i18n/use-translations';
import { RightArrowOutlined } from '@zeelool/icon';
import { useMemo, useState } from 'react';

interface GiftTeaserProps {
  giftData?: ProductDetailGiftPayload | null;
  isWeb?: boolean;
  className?: string;
  SDProductDetailsPageBtnClick?: (btnName: string, properties?: Record<string, unknown>) => void;
}

const GiftImage = ({
  gift,
  isWeb,
  className = '',
}: {
  gift: NonNullable<ReturnType<typeof buildProductDetailGiftViewModel>>['teaser'];
  isWeb?: boolean;
  className?: string;
}) => (
  <div
    className={`relative flex shrink-0 items-center justify-center overflow-hidden bg-auxiliary-light-gray ${className}`}
  >
    <Image
      src={gift.image}
      alt={gift.title}
      width={isWeb ? 560 : 375}
      height={isWeb ? 560 : 375}
      className={`h-auto object-contain ${gift.isGlass ? 'w-4/5 mix-blend-multiply' : 'w-full'}`}
    />
  </div>
);

export default function GiftTeaser({
  giftData,
  isWeb = false,
  className = '',
  SDProductDetailsPageBtnClick,
}: GiftTeaserProps) {
  const [open, setOpen] = useState(false);
  const t = useTranslations('goodsDetail');
  const giftViewModel = useMemo(() => buildProductDetailGiftViewModel(giftData), [giftData]);
  const gift = giftViewModel?.teaser;
  const singleDialogGift = useMemo<GiftSingleDialogData | null>(
    () =>
      gift
        ? {
            id: 'pdp-gift',
            name: gift.title,
            color: gift.color,
            images: gift.images,
            isGlass: gift.isGlass,
            sellPrice: gift.sellPrice,
            oldPrice: gift.oldPrice,
            stockText: gift.stockText,
            eventPeriod: gift.eventPeriod,
            rules: gift.rules,
          }
        : null,
    [gift],
  );

  if (!gift || !singleDialogGift) return null;

  const handleOpen = () => {
    reportProductDetailGiftInteraction(
      SDProductDetailsPageBtnClick,
      PDP_GIFT_INTERACTION_NAMES.entrance,
      gift.tracking,
    );
    setOpen(true);
  };

  const handleClose = () => setOpen(false);
  const handleRuleClick = () =>
    reportProductDetailGiftInteraction(
      SDProductDetailsPageBtnClick,
      PDP_GIFT_INTERACTION_NAMES.rule,
      gift.tracking,
    );
  const handleTierClick = () =>
    reportProductDetailGiftInteraction(
      SDProductDetailsPageBtnClick,
      PDP_GIFT_INTERACTION_NAMES.tier,
      gift.tracking,
    );
  const handleImagePreviewOpen = () =>
    reportProductDetailGiftInteraction(
      SDProductDetailsPageBtnClick,
      PDP_GIFT_INTERACTION_NAMES.image,
      gift.tracking,
    );

  return (
    <>
      <div
        onClick={handleOpen}
        className={`flex cursor-pointer items-center border text-left ${
          isWeb
            ? 'w-full rounded-4 border-[#dce0e8] bg-white py-[4px] pl-[4px] pr-[12px]'
            : 'rounded-[4px] border-[rgba(255,255,255,0.5)] bg-[rgba(255,255,255,0.5)] py-[3px] pl-[3px] pr-[8px]'
        } ${className}`}
      >
        <GiftImage
          gift={gift}
          isWeb={isWeb}
          className={isWeb ? 'h-[48px] w-[48px] rounded-[4px]' : 'h-[40px] w-[40px] rounded-[4px]'}
        />
        <span className={`${isWeb ? 'ml-[8px]' : 'ml-[6px]'} min-w-0 flex-1`}>
          <span
            className={`block font-bs text-[#282828] ${
              isWeb ? 'text-[14px] leading-[17px]' : 'text-[12px] leading-[14px]'
            }`}
          >
            {t('freeGift.teaserTitle')}
          </span>
          {gift.ruleText ? (
            <span
              className={`block text-[#808080] ${
                isWeb
                  ? 'mt-[4px] text-[12px] leading-[14px]'
                  : 'mt-[2px] truncate text-[10px] leading-[12px]'
              }`}
            >
              {gift.ruleText}
            </span>
          ) : null}
        </span>
        {isWeb && <RightArrowOutlined className="ml-8 text-[16px] text-[#282828]" />}
      </div>

      {giftViewModel.useDialogFlow ? (
        <GiftDialogFlow
          open={open}
          onClose={handleClose}
          activities={giftViewModel.dialogActivities}
          onRuleClick={handleRuleClick}
          onTierClick={handleTierClick}
          onImagePreviewOpen={handleImagePreviewOpen}
        />
      ) : (
        <GiftSingleDialog open={open} onClose={handleClose} gift={singleDialogGift} />
      )}
    </>
  );
}
